import { useEffect, useRef, useState } from "react";
import { useAudioRecorder } from "../hooks/use-audio-recorder";
import Button from "@/components/ui/button";

interface AudioRecorderProps {
	onRecordingComplete: (file: File, previewUrl: string, audioDuration?: number) => void;
	existingAudioFile?: File | null;
	existingAudioUrl?: string | null;
	onReset?: () => void;
	isDarkMode?: boolean;
}

function formatTime(seconds: number): string {
	const mins = Math.floor(seconds / 60);
	const secs = seconds % 60;
	return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
}

export default function AudioRecorder({
	onRecordingComplete,
	existingAudioFile,
	existingAudioUrl,
	onReset,
	isDarkMode,
}: AudioRecorderProps) {
	const {
		isRecording,
		isPaused,
		isProcessing,
		recordedBlob,
		recordedUrl,
		error,
		hasPermission,
		isInitializing,
		recordingTime,
		recordedDuration,
		initializeMicrophone,
		startRecording,
		stopRecording,
		pauseRecording,
		resumeRecording,
		resetRecording,
	} = useAudioRecorder();

	const hasNotifiedRef = useRef(false);
	const onRecordingCompleteRef = useRef(onRecordingComplete);

	const [localAudioUrl, setLocalAudioUrl] = useState<string | null>(null);

	onRecordingCompleteRef.current = onRecordingComplete;

	useEffect(() => {
		if (existingAudioFile) {
			const url = URL.createObjectURL(existingAudioFile);
			setLocalAudioUrl(url);
			return () => {
				URL.revokeObjectURL(url);
			};
		} else {
			setLocalAudioUrl(null);
		}
	}, [existingAudioFile]);

	const displayUrl = localAudioUrl || existingAudioUrl || recordedUrl;

	useEffect(() => {
		if (hasPermission === null && !existingAudioFile && !existingAudioUrl) {
			initializeMicrophone();
		}
	}, [
		hasPermission,
		initializeMicrophone,
		existingAudioFile,
		existingAudioUrl,
	]);

	useEffect(() => {
		if (recordedBlob && recordedUrl && !hasNotifiedRef.current) {
			hasNotifiedRef.current = true;
			const ext = recordedBlob.type.includes("mp4")
				? "m4a"
				: recordedBlob.type.includes("ogg")
					? "ogg"
					: "webm";
			const filename = `testimonial-audio-${Date.now()}.${ext}`;
			const file = new File([recordedBlob], filename, {
				type: recordedBlob.type,
			});
			console.log("[AudioRecorder] Recording complete, duration:", recordedDuration);
			onRecordingCompleteRef.current(file, recordedUrl, recordedDuration ?? undefined);
		}
	}, [recordedBlob, recordedUrl, recordedDuration]);

	const handleReRecord = () => {
		hasNotifiedRef.current = false;
		resetRecording();
		onReset?.();
		initializeMicrophone();
	};

	// Show playback if we have a recorded or existing audio
	if (displayUrl) {
		return (
			<div className="space-y-4">
				<div
					className={`rounded-xl border p-6 ${isDarkMode ? "bg-gray-900 border-gray-800" : "bg-gray-50 border-gray-200"}`}
				>
					<div className="flex items-center gap-3 mb-4">
						<div
							className={`w-10 h-10 rounded-full flex items-center justify-center ${isDarkMode ? "bg-green-900/30" : "bg-green-100"}`}
						>
							<svg
								className={`w-5 h-5 ${isDarkMode ? "text-green-400" : "text-green-600"}`}
								fill="none"
								viewBox="0 0 24 24"
								stroke="currentColor"
								strokeWidth={2}
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									d="M5 13l4 4L19 7"
								/>
							</svg>
						</div>
						<p
							className={`text-sm font-medium ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}
						>
							Audio recorded successfully
						</p>
					</div>
					<audio src={displayUrl} controls className="w-full" />
				</div>
				<div className="flex justify-center">
					<Button
						type="button"
						variant="soft"
						onClick={handleReRecord}
					>
						<span className="flex items-center gap-2">
							<svg
								className="w-4 h-4"
								fill="none"
								viewBox="0 0 24 24"
								stroke="currentColor"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
								/>
							</svg>
							Re-record
						</span>
					</Button>
				</div>
			</div>
		);
	}

	// Processing state
	if (isProcessing) {
		return (
			<div
				className={`rounded-xl p-8 flex items-center justify-center ${isDarkMode ? "bg-gray-900" : "bg-gray-100"}`}
			>
				<div className="text-center">
					<div
						className={`w-8 h-8 border-2 rounded-full animate-spin mx-auto mb-3 ${isDarkMode ? "border-gray-600 border-t-gray-300" : "border-gray-300 border-t-gray-600"}`}
					></div>
					<p
						className={`text-sm ${isDarkMode ? "text-gray-400" : "text-secondary"}`}
					>
						Processing audio...
					</p>
				</div>
			</div>
		);
	}

	// Error state
	if (error) {
		return (
			<div
				className={`rounded-xl border p-6 text-center ${isDarkMode ? "bg-red-900/20 border-red-800" : "bg-red-50 border-red-200"}`}
			>
				<svg
					className={`w-12 h-12 mx-auto mb-3 ${isDarkMode ? "text-red-400" : "text-red-400"}`}
					fill="none"
					viewBox="0 0 24 24"
					stroke="currentColor"
				>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth={2}
						d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
					/>
				</svg>
				<p
					className={`mb-4 ${isDarkMode ? "text-red-300" : "text-red-700"}`}
				>
					{error}
				</p>
				<Button
					type="button"
					variant="soft"
					onClick={() => initializeMicrophone()}
				>
					Try Again
				</Button>
			</div>
		);
	}

	// Initializing state
	if (isInitializing) {
		return (
			<div
				className={`rounded-xl p-8 flex items-center justify-center ${isDarkMode ? "bg-gray-900" : "bg-gray-100"}`}
			>
				<div className="text-center">
					<div
						className={`w-8 h-8 border-2 rounded-full animate-spin mx-auto mb-3 ${isDarkMode ? "border-gray-600 border-t-gray-300" : "border-gray-300 border-t-gray-600"}`}
					></div>
					<p
						className={`text-sm ${isDarkMode ? "text-gray-400" : "text-secondary"}`}
						style={{
							fontFamily: "var(--font-body)",
							fontWeight: "var(--font-body-weight)",
						}}
					>
						Initializing microphone...
					</p>
				</div>
			</div>
		);
	}

	// Permission denied
	if (hasPermission === false) {
		return (
			<div
				className={`rounded-xl p-8 flex items-center justify-center ${isDarkMode ? "bg-gray-900" : "bg-gray-100"}`}
			>
				<div className="text-center">
					<svg
						className={`w-12 h-12 mx-auto mb-3 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
						fill="none"
						viewBox="0 0 24 24"
						stroke="currentColor"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z"
						/>
					</svg>
					<p
						className={`mb-4 ${isDarkMode ? "text-gray-400" : "text-secondary"}`}
					>
						Microphone access is required to record an audio
						testimonial.
					</p>
					<Button
						type="button"
						variant="soft"
						onClick={() => initializeMicrophone()}
					>
						Enable Microphone
					</Button>
				</div>
			</div>
		);
	}

	// Recording UI
	return (
		<div className="space-y-4">
			<div
				className={`rounded-xl border p-8 text-center bg-white ${isDarkMode ? "border-gray-800 bg-gray-900" : "border-gray-200 bg-gray-50"}`}
			>
				{/* Animated microphone indicator */}
				<div className="relative w-16 h-16 mx-auto">
					{isRecording && !isPaused && (
						<>
							<span
								className={`absolute inset-0 rounded-full animate-ping opacity-30 ${isDarkMode ? "bg-red-900" : "bg-red-100"}`}
							></span>
							<span
								className={`absolute inset-2 rounded-full animate-ping opacity-20 animation-delay-200 ${isDarkMode ? "bg-red-900" : "bg-red-100"}`}
							></span>
						</>
					)}
					<div
						className={`relative w-16 h-16 rounded-full flex items-center justify-center ${isRecording ? (isPaused ? (isDarkMode ? "bg-yellow-900/30" : "bg-yellow-100") : isDarkMode ? "bg-red-900/30" : "bg-red-100") : isDarkMode ? "bg-gray-800" : "bg-gray-100"}`}
					>
						<svg
							className={`w-8 h-8 ${isRecording ? (isPaused ? "text-yellow-600" : "text-red-600") : isDarkMode ? "text-gray-400" : "text-gray-400"}`}
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
							strokeWidth={1.5}
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z"
							/>
						</svg>
					</div>
				</div>

				{/* Timer */}
				{isRecording && (
					<div className="flex items-center justify-center gap-2 mb-4">
						<span
							className={`w-2.5 h-2.5 rounded-full ${isPaused ? "bg-yellow-500" : "bg-red-500 animate-pulse"}`}
						></span>
						<span
							className={`text-lg font-medium ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}
						>
							{formatTime(recordingTime)}
						</span>
					</div>
				)}
			</div>

			{/* Controls */}
			<div className="flex justify-center gap-3">
				{!isRecording ? (
					<Button
						type="button"
						variant="light"
						onClick={startRecording}
						className="flex items-center gap-2"
					>
						<svg
							className="w-5 h-5 text-red-600"
							fill="currentColor"
							viewBox="0 0 24 24"
						>
							<circle cx="12" cy="12" r="10" />
						</svg>
						Start Recording
					</Button>
				) : (
					<>
						{isPaused ? (
							<Button
								type="button"
								variant="soft"
								onClick={resumeRecording}
								className="flex items-center gap-2"
							>
								<svg
									className="w-4 h-4"
									fill="currentColor"
									viewBox="0 0 24 24"
								>
									<path d="M8 5v14l11-7z" />
								</svg>
								Resume
							</Button>
						) : (
							<Button
								type="button"
								variant="soft"
								onClick={pauseRecording}
								className="flex items-center gap-2"
							>
								<svg
									className="w-4 h-4"
									fill="currentColor"
									viewBox="0 0 24 24"
								>
									<path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
								</svg>
								Pause
							</Button>
						)}
						<Button
							type="button"
							variant="light"
							onClick={stopRecording}
							className="flex items-center gap-2"
						>
							<svg
								className="w-4 h-4"
								fill="currentColor"
								viewBox="0 0 24 24"
							>
								<rect
									x="6"
									y="6"
									width="12"
									height="12"
									rx="2"
								/>
							</svg>
							Stop Recording
						</Button>
					</>
				)}
			</div>
		</div>
	);
}
